import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Input } from 'common/components/Input/Input'
import { Select } from 'common/components/Select/Select'
import type { IOption } from 'common/interfaces/IOption'
import { RegexService } from 'common/services/regexService'

import styles from './patientFormModal.module.scss'
import PhoneInput from 'common/components/PhoneInput/PhoneInput'
import { Col, Row } from 'antd'
import { Modal } from 'common/components/Modal'
import { Button } from 'common/components/Button/Button'
import { isValidPhoneNumber } from 'react-phone-number-input'
import SpinnerOverlay from 'common/components/SpinnerOverlay/SpinnerOverlay'

interface PatientFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: PatientFormType) => Promise<void>
  initialValues?: PatientFormType
  languages: IOption[]
}

const validationSchema = Yup.object({
  id: Yup.number().nullable().notRequired(),
  first_name: Yup.string()
    .required('First name is required')
    .min(2, 'Minimum 2 characters')
    .matches(RegexService.onlyLatin(), 'Only latin characters allowed'),
  last_name: Yup.string()
    .required('Last name is required')
    .min(2, 'Minimum 2 characters')
    .matches(RegexService.onlyLatin(), 'Only latin characters allowed'),
  phone: Yup.string()
    .nullable()
    .required('Please enter the mobile phone')
    .min(1, 'Please enter a valid mobile phone.')
    .test('is-valid-phone', 'Please enter a valid mobile phone', function (value) {
      return value && isValidPhoneNumber(value)
    }),
  language_type_code: Yup.number().required('Language is required').nullable(),
})

export type PatientFormType = Yup.InferType<typeof validationSchema>

const defaultInitialValues: PatientFormType = {
  id: undefined,
  first_name: '',
  last_name: '',
  phone: '',
  language_type_code: null,
}

export const PatientFormModal: React.FC<PatientFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  languages = [],
}) => {
  const isEdit = !!initialValues
  const formInitialValues = initialValues ?? defaultInitialValues

  const { values, errors, isSubmitting, handleChange, handleSubmit, setFieldValue, resetForm } =
    useFormik({
      initialValues: formInitialValues as PatientFormType,
      validationSchema,
      enableReinitialize: true,
      validateOnChange: false,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          await onSubmit(values as PatientFormType)
          handleClose()
        } catch (error) {
          console.error('Error submitting form:', error)
        } finally {
          setSubmitting(false)
        }
      },
    })

  function handleClose() {
    resetForm()
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      title={isEdit ? 'Edit Patient' : 'Add New Patient'}
      onCancel={handleClose}
      destroyOnClose>
      <form onSubmit={handleSubmit} className={styles.form}>
        <SpinnerOverlay isLoading={isSubmitting} />
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Input
                label='First Name'
                name='first_name'
                value={values.first_name}
                onChange={handleChange}
                error={errors.first_name}
                required
              />
            </Col>

            <Col xs={24} md={12}>
              <Input
                label='Last Name'
                name='last_name'
                value={values.last_name}
                onChange={handleChange}
                error={errors.last_name}
                required
              />
            </Col>

            <Col xs={24} md={12}>
              <PhoneInput
                label='Phone'
                value={values.phone}
                onChange={(value) => setFieldValue('phone', value)}
                required
                error={errors.phone}
              />
            </Col>

            <Col xs={24} md={12}>
              <Select
                label='Language'
                name='language'
                value={values.language_type_code}
                onChange={(value) => setFieldValue('language_type_code', value)}
                listOptions={languages}
                required
                error={errors.language_type_code}
              />
            </Col>
          </Row>
          <Row justify='end' className={styles.submitButton}>
            <Button disabled={isSubmitting} type='primary' htmlType='submit'>
              {isEdit ? 'Update' : 'Add'}
            </Button>
          </Row>
        </>
      </form>
    </Modal>
  )
}
